import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box, AppBar, Toolbar } from '@mui/material';
import { NotificationContext } from '../../../NotificationContext';

const defaultSx = {
    root: {
        background: (theme) => theme.palette.primary.default,
        color: (theme) => theme.palette.primary.contrastText,
    },
};

const AppBarSx = {
    bgcolor: 'background.paper',
    position: 'relative',
    borderBottom: '1px solid',
    borderColor: 'divider',
};

const ToolbarSx = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'flex-end',
};

/**
 * HeaderPage component that provides a consistent page layout with header functionality
 *
 * This component creates a page layout with:
 * - A customizable header section with title and subtitle
 * - Integration with the NotificationContext for toolbar content management
 * - Dynamic page title setting via react-helmet
 * - Flexible content area for children components
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - The main title for the page (used in document title and toolbar)
 * @param {string} [props.subtitle] - Optional subtitle text for the page
 * @param {React.ReactNode} [props.HeaderContent] - Custom content to render in the header toolbar
 * @param {React.ReactNode} [props.children] - Child components to render below the header
 * @param {Object} [props.sx] - Additional Material-UI sx styling to merge with default styles
 *
 * @example
 * <HeaderPage title="Dashboard">
 *   <DashboardContent />
 * </HeaderPage>
 *
 * @example
 * <HeaderPage
 *   title="User Management"
 *   subtitle="Manage user accounts and permissions"
 *   HeaderContent={<UserActions />}
 * >
 *   <UserList />
 * </HeaderPage>
 *
 * @returns {JSX.Element} The rendered HeaderPage component
 */
const HeaderPage = ({ title, subtitle, HeaderContent, children, sx }) => {
    const { setToolbarContent } = useContext(NotificationContext);
    useEffect(() => {
        setToolbarContent({ title, subtitle });
        return () => setToolbarContent({ title: '', subtitle: null, content: null });
    }, [title, subtitle]);

    return (
        <Box sx={{ ...defaultSx, ...sx }}>
            <Helmet>
                <title>{`${title} - Milk Moovement`}</title>
            </Helmet>
            <AppBar sx={AppBarSx} elevation={0}>
                <Toolbar disableGutters sx={ToolbarSx}>
                    {HeaderContent}
                </Toolbar>
            </AppBar>
            {children}
        </Box>
    );
};

export default HeaderPage;
